import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import moment from "moment";
import Select from "./Select";
import Input from "./Input";
import TrueInput from "./TrueInput";
import Checkbox from "./Checkbox";
import addressOptionsets from "../../options/address.json";
import {
  createEntity,
  getFieldName,
  getFieldText,
  getFieldDate,
  updateEntity,
} from "../../scripts/helperScripts";
import { getStates, getCounties } from "../services/soar";

function AddressModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  clientId,
  clientProgram,
  initialData,
  updateAddresses,
  setAddressBeingCreated,
  businessUnitName,
}) {
  const clientProgramAdmitDate =
    (clientProgram?.ccx_admitdate &&
      new Date(getFieldDate(clientProgram.ccx_admitdate))) ||
    new Date();
  const [addressData, setAddressData] = useState({
    ccx_addresstype: "100000000",
  });
  const [loadStatus, setLoadStatus] = useState({});
  const [addressStates, setAddressStates] = useState([]);
  const [addressCounties, setAddressCounties] = useState([]);
  const [errors, setErrors] = useState({});

  const stateOptions = useMemo(() => {
    if (!addressStates || addressStates.length === 0) {
      return [];
    }

    const options = addressStates.map((s) => ({
      _id: s.ccx_stateid,
      name: s.ccx_name,
      abbreviation: s.ccx_abbreviation,
    }));

    return options;
  }, [addressStates]);

  const countyOptions = useMemo(() => {
    if (
      !addressCounties ||
      addressCounties.length === 0 ||
      !addressData.ccx_state_lu
    ) {
      return [];
    }

    const options = addressCounties
      .filter((ac) => ac.ccx_state?.["#text"] === addressData.ccx_state_lu)
      .map((c) => ({
        _id: c.ccx_countyid,
        name: c.ccx_name,
        state: getFieldText(c.ccx_state),
      }))
      .sort((a, b) => a.ccx_name - b.ccx_name);

    return options;
  }, [addressCounties, addressData.ccx_state_lu]);

  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const joiSchema = {
    ccx_contactaddressid: Joi.string().allow(null),
    ccx_addresstype: Joi.string().required().label("Address Type"),
    ccx_receivestatement: Joi.boolean(),
    ccx_line1: Joi.string().required().label("Street Address 1"),
    ccx_city: Joi.string().required().label("City"),
    ccx_state_lu: Joi.string().required().label("State"),
    ccx_state: Joi.string(),
    ccx_postalcode: Joi.string()
      .regex(/^\d{5}(?:[-\s]\d{4})?$/)
      .label("Postal Code")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "string.regex.base":
              error.message = `Please enter a valid "Postal Code" (12345 or 12345-6789)`;
              break;
            default:
              return error;
          }
        });
        return errors;
      }),
    ccx_county_lu: Joi.string().allow(""),
    ccx_effectivedate: Joi.alternatives([
      Joi.date()
        .required()
        .max(
          moment(clientProgramAdmitDate).isAfter(moment())
            ? moment(clientProgramAdmitDate).toDate()
            : "now"
        )
        .min(new Date("1/1/1900"))
        .label("Effective Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.max":
                error.message = `"Effective Date" cannot be future dated beyond ${moment(
                  error.context.limit
                ).format("L")}`;
                break;
              case "date.min":
                error.message = `"Effective Date" cannot be before 1/1/1900`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
    ]),
    ccx_expirationdate: Joi.alternatives([
      Joi.date()
        .max(
          addressData.ccx_effectivedate &&
            moment(addressData.ccx_effectivedate).isAfter(moment())
            ? moment(addressData.ccx_effectivedate).toDate()
            : "now"
        )
        .min(new Date(addressData.ccx_effectivedate))
        .allow(null)
        .label("Expiration Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.min":
                error.message = `"Expiration Date" needs to be after "Effective Date" of ${moment
                  .utc(error.context.limit)
                  .format("L")}`;
                break;
              case "date.max":
                error.message = `"Expiration Date" cannot be future dated beyond "Effective Date" of ${moment
                  .utc(error.context.limit)
                  .format("L")}`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      Joi.string().valid(""),
    ]),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (initialData) {
      const obj = { ...initialData };
      delete obj.stateName;
      delete obj.countyName;
      setAddressData(obj);
    }
  }, [initialData]);

  useEffect(() => {
    if (!loadStatus.loaded && !loadStatus.loading) {
      const loadStatesAndCounties = async () => {
        setLoadStatus((ls) => {
          return { ...ls, loading: true };
        });
        const response = await Promise.all([getStates(), getCounties()]);

        if (!isMountedRef.current) {
          return;
        }

        setAddressStates(response[0]);
        setAddressCounties(response[1]);
        setLoadStatus((ls) => {
          return { ...ls, loaded: true, loading: false };
        });
      };

      loadStatesAndCounties();
    }
  }, [loadStatus.loaded, loadStatus.loading]);

  useEffect(() => {
    if (isOpen && !addressData.ccx_contactaddressid) {
      setAddressBeingCreated?.(true);
    }
  }, [isOpen, addressData.ccx_contactaddressid, setAddressBeingCreated]);

  const handleSave = () => {
    const obj = {
      ccx_addresstype: {
        type: "optionset",
        value: addressData.ccx_addresstype
          ? parseInt(addressData.ccx_addresstype)
          : null,
      },
      ccx_receivestatement: {
        type: "bool",
        value: addressData.ccx_receivestatement || false,
      },
      ccx_line1: {
        type: "string",
        value: addressData.ccx_line1 ? addressData.ccx_line1 : null,
      },
      ccx_city: {
        type: "string",
        value: addressData.ccx_city ? addressData.ccx_city : null,
      },
      ccx_effectivedate: {
        type: "datetime",
        value: addressData.ccx_effectivedate
          ? new Date(addressData.ccx_effectivedate)
          : null,
      },
      ccx_expirationdate: {
        type: "datetime",
        value: addressData.ccx_expirationdate
          ? new Date(addressData.ccx_expirationdate)
          : null,
      },
      ccx_state_lu: {
        type: "entity",
        value: {
          entityName: "ccx_state",
          entityId: addressData.ccx_state_lu
            ? getFieldText(addressData.ccx_state_lu) || addressData.ccx_state_lu
            : null,
        },
      },
      ccx_state: {
        type: "string",
        value: addressData.ccx_state ? addressData.ccx_state : null,
      },
      ccx_postalcode: {
        type: "string",
        value: addressData.ccx_postalcode ? addressData.ccx_postalcode : null,
      },
    };

    if (addressData.ccx_county_lu && addressData.ccx_county_lu !== "")
      obj.ccx_county_lu = {
        type: "entity",
        value: addressData.ccx_county_lu
          ? {
              entityName: "ccx_county",
              entityId: addressData.ccx_county_lu,
            }
          : "",
      };

    if (addressData.ccx_contactaddressid !== undefined) {
      const toastId = toast.loading("Address is being updated...", {
        type: toast.TYPE.INFO,
      });

      updateEntity(
        "ccx_contactaddress",
        addressData.ccx_contactaddressid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          updateAddresses?.(true);
          toast.update(toastId, {
            render: "Address has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating the Address.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        });
    } else {
      obj["ccx_parentid"] = {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: clientId,
        },
      };
      obj["ccx_effectivedate"] = {
        type: "datetime",
        value: addressData.ccx_effectivedate,
      };

      const toastId = toast.loading("Address is being created...", {
        type: toast.TYPE.INFO,
      });

      createEntity(
        "ccx_contactaddress",
        JSON.stringify(obj),
        safyInfo,
        (clientProgram !== undefined
          ? getFieldName(clientProgram.ccx_company)
          : businessUnitName
        ).split(" of ")[1]
      )
        .then(() => {
          updateAddresses?.(true);

          toast.update(toastId, {
            render: "Address has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        })
        .catch(() => {
          updateAddresses?.(false);

          toast.update(toastId, {
            render: "An error occurred while creating the Address.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            closeOnClick: true,
            autoClose: 5000,
          });
        })
        .finally(() => {
          setAddressBeingCreated?.(false);
        });
    }
  };

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }

    const data = { ...addressData };
    data[input.name] = input.value;
    setAddressData(data);
    setErrors(errs);
  };

  const handleStateChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }

    const selected = stateOptions.filter((as) => as._id === input.value);
    const data = { ...addressData };
    data[input.name] = input.value;
    data["ccx_state"] = selected[0] ? selected[0].abbreviation : null;
    setAddressData(data);
    setErrors(errs);
  };

  const handleChecked = ({ currentTarget: input }) => {
    input.value = input.checked;
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
      const data = { ...addressData };
      data[input.name] = input.checked;
      setAddressData(data);
    }

    setErrors(errs);
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(addressData, joiSchema, options);
    if (!error) return null;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;
    setErrors(validationErrors);
    return validationErrors;
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const resetForm = () => {
    setAddressData({
      ccx_addresstype: "100000000",
    });
    setErrors({});
  };

  const handleHideModal = () => {
    resetForm();
    hideModal();
  };

  return isOpen ? (
    <Modal show={isOpen} animation={false}>
      <Modal.Header>
        <Modal.Title>Address</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Select
          name="ccx_addresstype"
          label="Address Type"
          options={addressOptionsets.ccx_addresstype}
          value={addressData.ccx_addresstype}
          onChange={handleChange}
          error={errors.ccx_addresstype}
        />
        <div className="ps-4">
          <Checkbox
            name="ccx_receivestatement"
            label="Receive Statement"
            checked={addressData.ccx_receivestatement}
            onChange={handleChecked}
          />
        </div>
        <Input
          name="ccx_line1"
          label="Street Address 1"
          value={addressData.ccx_line1}
          onChange={handleChange}
          error={errors.ccx_line1}
        />
        <Input
          name="ccx_city"
          label="City"
          value={addressData.ccx_city}
          onChange={handleChange}
          error={errors.ccx_city}
        />
        <Select
          name="ccx_state_lu"
          label="State"
          options={stateOptions}
          value={addressData.ccx_state_lu}
          onChange={handleStateChange}
          error={errors.ccx_state_lu}
          disabled={stateOptions.length === 0}
        />
        <TrueInput
          name="ccx_postalcode"
          label="Postal Code"
          value={addressData.ccx_postalcode}
          onChange={handleChange}
          error={errors.ccx_postalcode}
          pattern="^\d{5}(?:[-\s]\d{4})?$"
        />
        <Select
          name="ccx_county_lu"
          label="County"
          options={countyOptions}
          value={addressData.ccx_county_lu}
          onChange={handleChange}
          error={errors.ccx_county_lu}
          disabled={countyOptions.length === 0}
        />
        <Input
          name="ccx_effectivedate"
          label="Effective Date"
          type="date"
          value={
            addressData.ccx_effectivedate &&
            moment(addressData.ccx_effectivedate).format(moment.HTML5_FMT.DATE)
          }
          onChange={handleChange}
          disabled={addressData.ccx_contactaddressid}
          error={errors.ccx_effectivedate}
        />
        {addressData.ccx_contactaddressid && (
          <Input
            name="ccx_expirationdate"
            label="Expiration Date"
            type="date"
            value={
              addressData.ccx_expirationdate
                ? moment
                    .utc(addressData.ccx_expirationdate)
                    .format(moment.HTML5_FMT.DATE)
                : ""
            }
            onChange={handleChange}
            error={errors.ccx_expirationdate}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {nextModal === undefined && (
          <>
            <button
              className="btn btn-danger"
              onClick={() => {
                setAddressBeingCreated?.(false);
                handleHideModal();
              }}
            >
              Cancel
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) {
                  handleSave();
                  handleHideModal();
                }
              }}
            >
              Submit
            </button>
            {/* <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) handleSaveAndAdd();
              }}
            >
              Submit and Add New
            </button> */}
          </>
        )}
        {nextModal && (
          <>
            <button
              className="btn btn-primary"
              onClick={() => {
                nextModal();
                updateAddresses?.(false);
              }}
            >
              Skip
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) {
                  handleSave();
                  resetForm();
                  nextModal();
                }
              }}
            >
              Submit and {isLastModal ? "End" : "Next"}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  ) : null;
}

export default AddressModal;
